import React, { useState, useContext, useEffect } from 'react'
import styled, { ThemeContext } from 'styled-components'

// Components
import Button from './Button'
import Icon from './icon/Icon'
import Text from './Text'

// Styles
import { shadows, fastTransition } from '../styles/effects'
import { mqFrom } from '../styles/mediaQueries'

// Types
import { IconType } from './icon/IconType'

interface IContextMenu {
  sections: ISection[]
  className?: string
  closeMenu?: () => void
}

export interface ISection {
  sectionLabel?: string
  links: IMenuLink[]
}

export interface IMenuLink {
  icon: IconType
  label: string
  marked?: boolean
  onClick?: () => void
  disabled?: boolean
  /** Handle for the e2e suite. The label is translated, so a spec that picks a
      menu entry by its words breaks the moment the app runs in Dutch. */
  'data-cy'?: string
}

type IProps = (
  // if necessary pass props to change button
  sections: ISection[],
  cardButton?: boolean
) => {
  buttonComponent: () => React.ReactNode
  showMenu: boolean
}

export const ContextMenu: React.FC<IContextMenu> = ({
  sections,
  closeMenu,
  className,
}) => {
  const theme = useContext(ThemeContext)

  return (
    <Menu className={className}>
      {sections.map((section, i) => (
        <React.Fragment key={`menu-section-${i}`}>
          {section.sectionLabel && (
            <Section textStyle="labelSmall" color="textLight">
              {section.sectionLabel}
            </Section>
          )}
          {section.links.map((link, index) => (
            <Link
              clickable={!!link.onClick && !link.disabled}
              onClick={() => {
                if (!link.disabled) {
                  link.onClick && link.onClick()
                  closeMenu && closeMenu()
                }
              }}
              key={`menu-${link.label}-${index}`}
              data-cy={link['data-cy']}
            >
              <Icon
                type={link.icon}
                color={link.disabled ? theme.colors.light : theme.colors.text}
              />
              <Text
                tag="span"
                textStyle="label"
                color={link.disabled ? 'light' : 'text'}
              >
                {link.label}
              </Text>
              {link.marked && <Mark />}
            </Link>
          ))}
        </React.Fragment>
      ))}
    </Menu>
  )
}

export const contextButton: IProps = (sections) => {
  const [showMenu, setShowMenu] = useState<boolean>(false)

  useEffect(() => {
    if (!window) return
    window.addEventListener('click', closeMenu)

    return () => {
      window.removeEventListener('click', closeMenu)
    }
  }, [])

  const closeMenu = () => {
    setShowMenu(false)
  }

  const openMenu = () => {
    setShowMenu(true)
  }

  const buttonComponent = () => (
    <ClickContainer onClick={(e) => e.stopPropagation()}>
      <Button
        color={showMenu ? 'primary' : 'primaryLight'}
        textColor={showMenu ? 'lightest' : 'primary'}
        hoverColor="primary"
        hoverTextColor="lightest"
        caption="•••"
        onClick={showMenu ? closeMenu : openMenu}
      />
      {showMenu && <ContextMenu sections={sections} closeMenu={closeMenu} />}
    </ClickContainer>
  )

  return {
    buttonComponent,
    showMenu,
  }
}

const ClickContainer = styled.div`
  ${mqFrom.M`
    position: relative;
  `}
`

const Menu = styled.ul`
  position: absolute;
  z-index: 20;
  top: 100%;
  right: 0;
  box-shadow: ${shadows.depthFour};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.lightest};
  list-style: none;
  overflow: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
  margin: 0;
  width: 100%;

  ${mqFrom.M`
    top: calc(100% + 4px);
    width: unset;
  `}
`

const Section = styled(Text)`
  text-transform: uppercase;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.textLightest};
  padding: 4px 12px;
  cursor: default;
`

const Link = styled.li<{ clickable: boolean }>`
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  display: flex;
  align-items: center;
  padding: 16px 12px 16px 12px;
  background-color: ${({ theme }) => theme.colors.lightest};
  transition: background-color ${fastTransition};
  min-width: 168px;
  white-space: nowrap;

  > span {
    transition: color ${fastTransition};
    margin-right: 12px;
  }

  &:not(&:last-of-type) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.primaryLight};
  }

  ${({ clickable, theme }) =>
    clickable &&
    `
    &:hover {
      background-color: ${theme.colors.primaryLight};
      > span {
        color: ${theme.colors.primary};
      }

      svg > path {
        fill: ${theme.colors.primary};
      }
    }
  `}
`

const Mark = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.secondary};
  margin-left: auto;
`
